import { OrganisationsService } from "@/client"
import type { OrganisationRead } from "@/client"
import { LogoCropper } from "@/components/Common/LogoCropper"
import { VisionButton, VisionCard } from "@/components/VisionUI"
import { useColorModeValue } from "@/components/ui/color-mode"
import { useOrganization } from "@/contexts/OrganizationContext"
import { useSemanticColors } from "@/hooks/useSemanticColors"
import { colors } from "@/theme"
import { resolveLogoUrl } from "@/utils"
import {
  Box,
  Flex,
  Grid,
  Heading,
  Image,
  Separator,
  Spinner,
  Text,
} from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { FiImage, FiTrash2, FiUpload } from "react-icons/fi"

// Allowed MIME types for logo upload — explicit whitelist (no SVG, no GIF).
// SVG is excluded because it can carry inline scripts (XSS risk in img src),
// GIF because animated logos break the sidebar.
const ALLOWED_MIME_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
])

// Match the backend cap (organisation.py upload_organisation_logo).
const MAX_FILE_BYTES = 10 * 1024 * 1024

export const Route = createFileRoute("/_layout/admin-branding")({
  component: AdminBranding,
})

function OrgLogoCard({ org }: { org: OrganisationRead }) {
  const sc = useSemanticColors()
  const isLight = useColorModeValue(true, false)
  const queryClient = useQueryClient()
  const { t } = useTranslation("admin")
  const fileRef = useRef<HTMLInputElement>(null)
  // The blob URL of the originally-selected file, fed to the cropper.
  const [rawImageSrc, setRawImageSrc] = useState<string | null>(null)
  // Preview of the cropped result, also a blob URL. Shown after the cropper applies.
  const [preview, setPreview] = useState<string | null>(null)
  // The cropped Blob to upload on Save. Null until the user finishes cropping.
  const [croppedBlob, setCroppedBlob] = useState<Blob | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [cropperOpen, setCropperOpen] = useState(false)

  // Revoke object URLs when they go out of scope to avoid leaks.
  useEffect(() => {
    return () => {
      if (rawImageSrc) URL.revokeObjectURL(rawImageSrc)
    }
  }, [rawImageSrc])
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview)
    }
  }, [preview])

  const cardBorder = isLight ? "#E2E8F0" : colors.glass.whiteAlpha[200]
  const subtleText = isLight ? "#718096" : "gray.400"
  const dropzoneBg = isLight ? "rgba(0,0,0,0.02)" : colors.glass.whiteAlpha[50]

  const uploadMutation = useMutation({
    mutationFn: (blob: Blob) =>
      OrganisationsService.uploadOrganisationLogo({
        organisationId: org.id,
        formData: { file: blob },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] })
      setPreview(null)
      setCroppedBlob(null)
      setRawImageSrc(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () =>
      OrganisationsService.deleteOrganisationLogo({ organisationId: org.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] })
    },
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Always reset the input so the same file can be re-picked after cancel.
    if (fileRef.current) fileRef.current.value = ""
    if (!file) return

    setValidationError(null)

    // MIME whitelist (defence-in-depth — backend validates again).
    if (!ALLOWED_MIME_TYPES.has(file.type)) {
      setValidationError(t("branding.invalidFileType"))
      return
    }
    if (file.size > MAX_FILE_BYTES) {
      setValidationError(t("branding.fileTooLarge"))
      return
    }

    setRawImageSrc(URL.createObjectURL(file))
    setCropperOpen(true)
  }

  const handleCropConfirm = (blob: Blob) => {
    if (preview) URL.revokeObjectURL(preview)
    setPreview(URL.createObjectURL(blob))
    setCroppedBlob(blob)
    setCropperOpen(false)
  }

  const handleCropCancel = () => {
    setCropperOpen(false)
    if (rawImageSrc) URL.revokeObjectURL(rawImageSrc)
    setRawImageSrc(null)
  }

  const handleUpload = () => {
    if (croppedBlob) uploadMutation.mutate(croppedBlob)
  }

  const handleCancel = () => {
    if (preview) URL.revokeObjectURL(preview)
    if (rawImageSrc) URL.revokeObjectURL(rawImageSrc)
    setPreview(null)
    setRawImageSrc(null)
    setCroppedBlob(null)
    setValidationError(null)
  }

  // preview is a local blob URL (always absolute); org.logo_url needs resolving
  const currentLogo = preview ?? resolveLogoUrl(org.logo_url)

  return (
    <VisionCard variant="elevated" p={6}>
      <Flex direction="column" gap={4}>
        <Box>
          <Text fontSize="md" fontWeight="700" color={sc.heading}>
            {org.name}
          </Text>
          {org.description && (
            <Text fontSize="sm" color={subtleText} mt={1}>
              {org.description}
            </Text>
          )}
        </Box>

        <Separator borderColor={cardBorder} />

        <Flex
          align="center"
          justify="center"
          minH="120px"
          border="2px dashed"
          borderColor={currentLogo ? "transparent" : cardBorder}
          borderRadius="12px"
          bg={currentLogo ? "transparent" : dropzoneBg}
          overflow="hidden"
          cursor={currentLogo ? "default" : "pointer"}
          onClick={() => !currentLogo && fileRef.current?.click()}
        >
          {currentLogo ? (
            <Image
              src={currentLogo}
              alt={`${org.name} logo`}
              maxH="100px"
              maxW="100%"
              objectFit="contain"
              borderRadius="8px"
            />
          ) : (
            <Flex direction="column" align="center" gap={2} py={6}>
              <FiImage size={32} color={subtleText} />
              <Text fontSize="sm" color={subtleText}>
                {t("branding.noLogo")}
              </Text>
            </Flex>
          )}
        </Flex>

        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        {validationError && (
          <Text fontSize="sm" color="red.400">
            {validationError}
          </Text>
        )}
        {uploadMutation.isError && (
          <Text fontSize="sm" color="red.400">
            {t("branding.uploadError")}
          </Text>
        )}
        {deleteMutation.isError && (
          <Text fontSize="sm" color="red.400">
            {t("branding.deleteError")}
          </Text>
        )}

        <LogoCropper
          open={cropperOpen}
          imageSrc={rawImageSrc}
          onCancel={handleCropCancel}
          onConfirm={handleCropConfirm}
        />

        <Flex gap={2} wrap="wrap">
          {preview ? (
            <>
              <VisionButton
                variant="brand"
                size="sm"
                flex="1"
                onClick={handleUpload}
                loading={uploadMutation.isPending}
              >
                <FiUpload />
                {t("branding.save")}
              </VisionButton>
              <VisionButton
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                disabled={uploadMutation.isPending}
              >
                {t("branding.cancel")}
              </VisionButton>
            </>
          ) : (
            <VisionButton
              variant="ghost"
              size="sm"
              flex="1"
              onClick={() => fileRef.current?.click()}
            >
              <FiUpload />
              {org.logo_url
                ? t("branding.changeLogo")
                : t("branding.uploadLogo")}
            </VisionButton>
          )}
          {org.logo_url && !preview && (
            <VisionButton
              variant="ghost"
              size="sm"
              color="red.400"
              _hover={{ bg: "red.50" }}
              loading={deleteMutation.isPending}
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (window.confirm(t("branding.deleteLogoConfirm"))) {
                  deleteMutation.mutate()
                }
              }}
            >
              <FiTrash2 />
            </VisionButton>
          )}
        </Flex>
      </Flex>
    </VisionCard>
  )
}

function SectionHeading({
  label,
  description,
}: { label: string; description?: string }) {
  const sc = useSemanticColors()
  return (
    <Box mb={4}>
      <Heading size="md" color={sc.heading} mb={1}>
        {label}
      </Heading>
      {description && (
        <Text fontSize="sm" color={sc.textMuted}>
          {description}
        </Text>
      )}
    </Box>
  )
}

function AdminBranding() {
  const sc = useSemanticColors()
  const isLight = useColorModeValue(true, false)
  const { t } = useTranslation("admin")
  const { organizations, isLoading } = useOrganization()

  // Split into org-level (no parent) and departments (has parent)
  const hasDepartments = organizations.some((o) => !!o.parent_id)
  const rootOrgs = organizations.filter((o) => !o.parent_id)
  const departments = organizations.filter((o) => !!o.parent_id)

  const separatorColor = isLight ? "#E2E8F0" : "whiteAlpha.200"

  return (
    <Box>
      <Box mb={8}>
        <Heading size="xl" color={sc.heading} mb={2}>
          {t("branding.title")}
        </Heading>
        <Text color={sc.textMuted}>{t("branding.subtitle")}</Text>
      </Box>

      {isLoading ? (
        <Flex justify="center" py={12}>
          <Spinner size="lg" />
        </Flex>
      ) : hasDepartments ? (
        <>
          {/* Organization section */}
          {rootOrgs.length > 0 && (
            <Box mb={10}>
              <SectionHeading
                label={t("branding.organizationSection")}
                description={t("branding.orgLogoDesc")}
              />
              <Grid
                templateColumns={{
                  base: "1fr",
                  md: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                }}
                gap={6}
              >
                {rootOrgs.map((org) => (
                  <OrgLogoCard key={org.id} org={org} />
                ))}
              </Grid>
            </Box>
          )}

          <Separator borderColor={separatorColor} mb={8} />

          {/* Departments section */}
          <Box>
            <SectionHeading
              label={t("branding.departmentsSection")}
              description={t("branding.deptLogoDesc")}
            />
            <Grid
              templateColumns={{
                base: "1fr",
                md: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
              }}
              gap={6}
            >
              {departments.map((org) => (
                <OrgLogoCard key={org.id} org={org} />
              ))}
            </Grid>
          </Box>
        </>
      ) : (
        /* No hierarchy yet — show all as departments */
        <Box>
          <SectionHeading
            label={t("branding.departmentsSection")}
            description={t("branding.subtitle")}
          />
          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            }}
            gap={6}
          >
            {organizations.map((org) => (
              <OrgLogoCard key={org.id} org={org} />
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  )
}
